import React, { useState, useEffect } from 'react';
import { useSession } from '../Utils/SessionContext';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import CustomTooltip from '../Utils/CustomTooltip';
import toast from 'react-hot-toast';
import Editor from '@monaco-editor/react';

const AllPrompts = () => {
  const { user, logout } = useSession();
  const navigate = useNavigate();
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Selection states
  const [selectedPrompt, setSelectedPrompt] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedEvaluationType, setSelectedEvaluationType] = useState('');
  
  // Prompt content states
  const [promptContent, setPromptContent] = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptError, setPromptError] = useState(null);
  
  // Editor states for tracking changes and saving
  const [promptChanges, setPromptChanges] = useState({}); // Track changes for each prompt
  const [schemaChanges, setSchemaChanges] = useState({}); // Track changes for each schema
  const [savingPrompts, setSavingPrompts] = useState({}); // Track saving state for each prompt
  const [focusedEditor, setFocusedEditor] = useState({}); // Track which editor is focused for each prompt

  // Fetch all prompts when component mounts
  useEffect(() => {
    const fetchAllPrompts = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Make GET request to fetch all unique prompts
        const response = await fetch(API_ENDPOINTS.GET_ALL_UNIQUE_PROMPTS, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${user.access_token}`
          }
        });
        
        // Check if the response is ok
        if (!response.ok) {
          const error = new Error(`Server responded with status: ${response.status}`);
          error.status = response.status;
          throw error;
        }
        
        // Parse the response data
        const responseData = await response.json();
        console.log('All prompts fetched:', responseData);
        
        // Set the prompts data
        setPrompts(responseData);
        
      } catch (err) {
        console.error('Error fetching all prompts:', err);
        
        // If it's a 401 error, it's an expired session - log out and redirect to login
        if (err.status === 401) {
          console.log('Session expired or unauthorized. Redirecting to login page.');
          logout();
          navigate('/login');
          return;
        }
        
        setError('Failed to load prompts. Please try again later.');
        toast.error('Failed to load prompts');
      } finally {
        setLoading(false);
      }
    };
    
    if (user && user.access_token) {
      fetchAllPrompts();
    }
  }, [user, logout, navigate]);

  // Get available category objects from selected prompt (full objects like in ActionPrompts)
  const getAvailableCategories = () => {
    if (!selectedPrompt) return [];
    const prompt = prompts.find(p => p.promptnameid === selectedPrompt);
    if (!prompt || !prompt.categories) return [];
    
    // Return the full category objects (each contains mainCategory and subCategory arrays)
    return prompt.categories;
  };

  // Get available evaluation types from selected prompt
  const getAvailableEvaluationTypes = () => {
    if (!selectedPrompt) return [];
    const prompt = prompts.find(p => p.promptnameid === selectedPrompt);
    return prompt?.evaluation_types || [];
  };

  // Fetch specific prompt content
  const fetchPromptContent = async () => {
    if (!user?.access_token || !selectedPrompt || !selectedCategory || !selectedEvaluationType) return;
    
    try {
      setPromptLoading(true);
      setPromptError(null);
      
      // Use the same request format as ActionPrompts.js but with promptNameID instead of action
      const requestBody = {
        role: selectedCategory,        // Send the full category object (like role in ActionPrompts)
        promptNameID: selectedPrompt,  // Send the prompt name as promptNameID (instead of action)
        type: selectedEvaluationType   // Send the evaluation type as type
      };
      
      console.log('Fetching prompt with filters:', requestBody);
      
      const response = await fetch(API_ENDPOINTS.GET_PROMPT, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = new Error(`Server responded with status: ${response.status}`);
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      console.log('Prompt content loaded:', data);
      
      setPromptContent(data);
      
    } catch (err) {
      console.error('Error loading prompt content:', err);
      
      // If it's a 401 error, it's an expired session - log out and redirect to login
      if (err.status === 401) {
        console.log('Session expired or unauthorized. Redirecting to login page.');
        logout();
        navigate('/login');
        return;
      }
      
      setPromptError('Failed to load prompt content. Please try again later.');
      toast.error('Failed to load prompt content');
    } finally {
      setPromptLoading(false);
    }
  };

  // Fetch prompt content when selections change
  useEffect(() => {
    if (selectedPrompt && selectedCategory && selectedEvaluationType) {
      fetchPromptContent();
    } else {
      setPromptContent(null);
    }
  }, [selectedPrompt, selectedCategory, selectedEvaluationType]);

  // Reset dependent selections when prompt changes
  useEffect(() => {
    setSelectedCategory('');
    setSelectedEvaluationType('');
    setPromptContent(null);
    setPromptError(null);
    setPromptLoading(false);
  }, [selectedPrompt]);

  // Reset changes when prompt content changes
  useEffect(() => {
    setPromptChanges({});
    setSchemaChanges({});
  }, [promptContent]);

  // Handle prompt text change
  const handlePromptChange = (promptId, newValue, originalValue) => {
    setPromptChanges(prev => ({
      ...prev,
      [promptId]: {
        hasChanges: newValue !== originalValue,
        newValue: newValue,
        originalValue: originalValue
      }
    }));
  };

  // Handle schema text change
  const handleSchemaChange = (promptId, newValue, originalValue) => {
    setSchemaChanges(prev => ({
      ...prev,
      [promptId]: {
        hasChanges: newValue !== originalValue,
        newValue: newValue,
        originalValue: originalValue
      }
    }));
  };

  // Save prompt function
  const savePrompt = async (promptId, promptText, schemaText) => {
    if (!user?.access_token) return;
    
    try {
      setSavingPrompts(prev => ({ ...prev, [promptId]: true }));
      
      // Parse schema text back to JSON object for database storage
      let parsedSchema;
      try {
        parsedSchema = schemaText ? JSON.parse(schemaText) : null;
      } catch (parseError) {
        console.error('Error parsing schema JSON:', parseError);
        toast.error('Invalid JSON in schema. Please check the syntax.');
        setSavingPrompts(prev => ({ ...prev, [promptId]: false }));
        return;
      }
      
      const requestBody = {
        id: promptId,
        prompt: promptText,
        output_json_schema: parsedSchema
      };
      
      console.log('Saving prompt and schema:', requestBody);
      
      const response = await fetch(API_ENDPOINTS.SAVE_PROMPTS, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Prompt and schema saved successfully:', data);
      
      // Clear the changes for this prompt and schema
      setPromptChanges(prev => {
        const updated = { ...prev };
        delete updated[promptId];
        return updated;
      });
      
      setSchemaChanges(prev => {
        const updated = { ...prev };
        delete updated[promptId];
        return updated;
      });
      
      // Update the original prompt content
      setPromptContent(prev => {
        if (Array.isArray(prev)) {
          return prev.map(prompt => 
            prompt.prompt_id === promptId 
              ? { ...prompt, prompt: promptText, output_json_schema: parsedSchema }
              : prompt
          );
        }
        return prev;
      });
      
      toast.success('Prompt saved successfully');
      
    } catch (err) {
      console.error('Error saving prompt:', err);
      toast.error('Failed to save prompt. Please try again.');
    } finally {
      setSavingPrompts(prev => ({ ...prev, [promptId]: false }));
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-600">Loading prompts...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => window.location.reload()}
                  className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200 transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Column 1 - Prompts */}
      <div className={`${selectedPrompt ? 'w-16 hover:w-80' : 'w-80'} flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col transition-all duration-300 overflow-hidden`}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center">
            <span className="mr-2">→</span>
            <span className="whitespace-nowrap">PROMPTS</span>
          </h3>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          {prompts.length > 0 && (
            <div className="space-y-2" style={{ minWidth: '288px' }}>
              {prompts.map((prompt) => (
                <button
                  key={prompt.promptnameid}
                  onClick={() => {
                    // Reset all filters and content when selecting a prompt
                    setSelectedPrompt(prompt.promptnameid);
                    setSelectedCategory('');
                    setSelectedEvaluationType('');
                    setPromptContent(null);
                    setPromptError(null);
                    setPromptLoading(false);
                  }}
                  className={`w-full text-left p-3 border rounded text-sm transition-colors whitespace-nowrap ${
                    selectedPrompt === prompt.promptnameid
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {prompt.promptnameid}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Column 2 - Categories */}
      <div className={`${selectedCategory ? 'w-16 hover:w-80' : selectedPrompt ? 'w-80' : 'w-16'} flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col transition-all duration-300 overflow-hidden`}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center">
            <span className="mr-2">→</span>
            <span className="whitespace-nowrap">CATEGORIES</span>
          </h3>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          {selectedPrompt && getAvailableCategories().length > 0 && (
            <div className="space-y-2" style={{ minWidth: '288px' }}>
              {getAvailableCategories().map((category, index) => (
                <button
                  key={index}
                  onClick={() => setSelectedCategory(category)}
                  className={`w-full text-left p-3 border rounded text-sm transition-colors ${
                    selectedCategory === category
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium mb-1 whitespace-nowrap overflow-hidden text-ellipsis">
                    {category.mainCategory ? category.mainCategory.join(', ') : 'No main categories'}
                  </div>
                  {category.subCategory && category.subCategory.length > 0 && (
                    <div className="text-xs text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                      {category.subCategory.slice(0, 3).join(', ')}
                      {category.subCategory.length > 3 && ` +${category.subCategory.length - 3} more`}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
          {selectedPrompt && getAvailableCategories().length === 0 && (
            <div className="text-center text-gray-500 py-8" style={{ minWidth: '288px' }}>
              <p className="text-sm">No categories available for this prompt</p>
            </div>
          )}
          {!selectedPrompt && (
            <div className="text-center text-gray-500 py-8" style={{ minWidth: '288px' }}>
              <p className="text-sm">Select a prompt first</p>
            </div>
          )}
        </div>
      </div>

      {/* Column 3 - Evaluation Types */}
      <div className={`${selectedEvaluationType ? 'w-16 hover:w-80' : selectedCategory ? 'w-80' : 'w-16'} flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col transition-all duration-300 overflow-hidden`}>
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800 flex items-center">
            <span className="mr-2">→</span>
            <span className="whitespace-nowrap">EVALUATION TYPES</span>
          </h3>
        </div>
        <div className="p-4 flex-1 overflow-y-auto">
          {selectedPrompt && getAvailableEvaluationTypes().length > 0 && (
            <div className="space-y-2" style={{ minWidth: '288px' }}>
              {getAvailableEvaluationTypes().map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedEvaluationType(type)}
                  className={`w-full text-left p-3 border rounded text-sm transition-colors whitespace-nowrap ${
                    selectedEvaluationType === type
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          )}
          {selectedPrompt && getAvailableEvaluationTypes().length === 0 && (
            <div className="text-center text-gray-500 py-8" style={{ minWidth: '288px' }}>
              <p className="text-sm">No evaluation types available for this prompt</p>
            </div>
          )}
          {!selectedPrompt && (
            <div className="text-center text-gray-500 py-8" style={{ minWidth: '288px' }}>
              <p className="text-sm">Select a prompt first</p>
            </div>
          )}
        </div>
      </div>

      {/* Column 4 - JSON Content */}
      <div className="flex-1 bg-white overflow-hidden">
        <div className="h-full overflow-auto p-6">
          {promptLoading && (
            <div className="text-center text-gray-500 mt-20">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-lg">Loading prompt content...</p>
            </div>
          )}
          
          {promptError && (
            <div className="text-center text-red-500 mt-20">
              <p className="text-lg mb-4">{promptError}</p>
              <button 
                onClick={fetchPromptContent}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                Retry
              </button>
            </div>
          )}
          
          {promptContent && !promptLoading && !promptError && Array.isArray(promptContent) && promptContent.length > 0 && (
            <div className="flex gap-4 h-full">
              {promptContent.map((prompt, index) => {
                const promptId = prompt.prompt_id || index;
                const hasPromptChanges = promptChanges[promptId]?.hasChanges || false;
                const hasSchemaChanges = schemaChanges[promptId]?.hasChanges || false;
                const hasChanges = hasPromptChanges || hasSchemaChanges;
                const currentPromptValue = promptChanges[promptId]?.newValue || prompt.prompt || '';
                
                // Convert schema to string (schema is always a JSON object)
                const schemaValue = JSON.stringify(prompt.output_json_schema || {}, null, 2);
                
                const currentSchemaValue = schemaChanges[promptId]?.newValue || schemaValue;
                const isSaving = savingPrompts[promptId] || false;
                
                return (
                  <div key={promptId} className="flex-shrink-0 min-w-[860px] bg-white rounded-lg border flex flex-col h-full" style={{ overflow: 'visible' }}>
                    {/* Column Header */}
                    <div className="p-4 border-b bg-blue-50 rounded-t-lg flex-shrink-0 flex justify-between items-center" style={{ overflow: 'visible', position: 'relative', zIndex: 10 }}>
                      <div>
                        <CustomTooltip content={prompt.description || 'No description available'} position="bottom">
                          <h4 className="font-semibold text-gray-800 text-sm cursor-help">
                            {prompt.promptNameID || selectedPrompt} (id:{prompt.prompt_id}) 
                          </h4>
                        </CustomTooltip>
                        <p className="text-xs text-gray-600 mt-1">
                          {selectedCategory && selectedCategory.mainCategory ? selectedCategory.mainCategory.join(', ') : 'No category'} - {selectedEvaluationType}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        {/* Unsaved changes indicator */}
                        {hasChanges && (
                          <span className="text-orange-600 text-xs">Unsaved changes</span>
                        )}
                        
                        {/* Save Button */}
                        <button
                          onClick={() => savePrompt(promptId, currentPromptValue, currentSchemaValue)}
                          disabled={!hasChanges || isSaving}
                          className={`px-3 py-1 text-xs font-medium rounded transition-colors ${
                            hasChanges && !isSaving
                              ? 'bg-blue-500 text-white hover:bg-blue-600'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                    
                    {/* Column Content */}
                    <div className="flex-1 flex flex-col min-h-0 gap-2 p-2">
                      {/* Prompt Editor */}
                      <div className={`flex flex-col min-h-0 ${
                        focusedEditor[promptId] === 'prompt' ? 'flex-[9]' : 
                        focusedEditor[promptId] === 'schema' ? 'flex-1' : 'flex-[9]'
                      }`}>
                        <div className="mb-2">
                          <h5 className="text-xs font-medium text-gray-700 mb-1">PROMPT</h5>
                        </div>
                        <div className="flex-1 border rounded overflow-hidden min-h-0">
                          <Editor
                            key={`prompt-${promptId}`}
                            height="100%"
                            defaultLanguage="markdown"
                            value={currentPromptValue}
                            onChange={(value) => handlePromptChange(promptId, value || '', prompt.prompt || '')}
                            onMount={(editor) => {
                              editor.onDidFocusEditorText(() => {
                                setFocusedEditor(prev => ({ ...prev, [promptId]: 'prompt' }));
                              });
                            }}
                            options={{
                              readOnly: false,
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                              wordWrap: 'on',
                              fontSize: 14,
                              lineNumbers: 'off',
                              folding: false,
                              glyphMargin: false,
                              lineDecorationsWidth: 0,
                              lineNumbersMinChars: 0,
                              renderLineHighlight: 'none',
                              automaticLayout: true,
                              'breadcrumbs.enabled': false
                            }}
                            theme="vs"
                          />
                        </div>
                      </div>
                      
                      {/* Schema Editor */}
                      <div className={`flex flex-col min-h-0 ${
                        focusedEditor[promptId] === 'schema' ? 'flex-[9]' : 
                        focusedEditor[promptId] === 'prompt' ? 'flex-1' : 'flex-1'
                      }`}>
                        <div className="mb-2">
                          <h5 className="text-xs font-medium text-gray-700 mb-1">OUTPUT JSON SCHEMA</h5>
                        </div>
                        <div className="flex-1 border rounded overflow-hidden min-h-0">
                          <Editor
                            key={`schema-${promptId}`}
                            height="100%"
                            defaultLanguage="json"
                            value={currentSchemaValue}
                            onChange={(value) => handleSchemaChange(promptId, value || '', schemaValue)}
                            onMount={(editor) => {
                              editor.onDidFocusEditorText(() => {
                                setFocusedEditor(prev => ({ ...prev, [promptId]: 'schema' }));
                              });
                            }}
                            options={{
                              readOnly: false,
                              minimap: { enabled: false },
                              scrollBeyondLastLine: false,
                              wordWrap: 'on',
                              fontSize: 14,
                              lineNumbers: 'on',
                              folding: false,
                              glyphMargin: false,
                              renderLineHighlight: 'none',
                              automaticLayout: true,
                              tabSize: 2,
                              insertSpaces: true,
                              'breadcrumbs.enabled': false
                            }}
                            theme="vs"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {!selectedPrompt && (
            <div className="text-center text-gray-500 mt-20">
              <p className="text-lg">Select a prompt to get started</p>
              <p className="text-sm mt-2">Choose from {prompts.length} available prompts</p>
            </div>
          )}
          
          {selectedPrompt && !selectedCategory && (
            <div className="text-center text-gray-500 mt-20">
              <p className="text-lg">Select a category</p>
              <p className="text-sm mt-2">Choose from the available categories for "{selectedPrompt}"</p>
            </div>
          )}
          
          {selectedPrompt && selectedCategory && !selectedEvaluationType && (
            <div className="text-center text-gray-500 mt-20">
              <p className="text-lg">Select an evaluation type</p>
              <p className="text-sm mt-2">Choose from the available evaluation types</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllPrompts;
